import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { InvitationDetails } from '../types';

interface InvitationLinkSectionProps {
  invitation: InvitationDetails;
  /** Who the personal link may be shared with. */
  personalText: string;
  testId: string;
  deliveryTestId: string;
}

/**
 * The invitation link with a copy button and the mail status, so admin can
 * pass the link on when the mail does not arrive. Shared by the contractor
 * and client create pages.
 */
export function InvitationLinkSection({ invitation, personalText, testId, deliveryTestId }: InvitationLinkSectionProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const linkInput = useRef<HTMLInputElement>(null);
  const url = invitation.registrationUrl;

  // A resent invitation has a new link.
  useEffect(() => setCopied(false), [url]);

  const copyLink = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // No clipboard API (or no permission): select the text and fall back to execCommand.
      linkInput.current?.select();
      try { document.execCommand('copy'); } catch { /* the selected text can still be copied by hand */ }
    }
    setCopied(true);
  };

  if (!url) return null;
  return <section className="company-setup-link mb-3" aria-labelledby={`${testId}-title`} data-testid={testId}>
    <h3 id={`${testId}-title`} className="h6 fw-bold">{t('companySetup.inviteLink.title')}</h3>
    <label className="form-label" htmlFor={`${testId}-url`}>{t('companySetup.inviteLink.label')}</label>
    <div className="d-flex flex-column flex-sm-row gap-2">
      <input ref={linkInput} id={`${testId}-url`} type="text" className="form-control company-setup-link__input" value={url} readOnly onFocus={event => event.currentTarget.select()} />
      <button type="button" className="btn btn-outline-primary company-setup-link__copy" onClick={copyLink}>
        <i className={`feather-${copied ? 'check' : 'copy'} me-1`} aria-hidden="true"></i>
        {t('companySetup.inviteLink.copy')}
      </button>
    </div>
    <p className="small text-success mb-0 mt-1" aria-live="polite">{copied ? t('companySetup.inviteLink.copied') : ''}</p>
    <p className="small text-muted mb-1">{personalText}</p>
    {invitation.emailStatus && <p className="small mb-0" data-testid={deliveryTestId}>
      {t('companySetup.inviteLink.delivery', { status: t(`companySetup.delivery.${invitation.emailStatus}`) })}
      {invitation.emailStatus === 'failed' && invitation.emailFailureReason && <span className="d-block text-danger text-break">{t('companySetup.inviteLink.failureReason', { reason: invitation.emailFailureReason })}</span>}
    </p>}
  </section>;
}
